// The Usage demo's fixture types. Everything on the Usage screens is read
// from these shapes; nothing comes from the API.

export type ContractType = "seat" | "consumption";

/** Why a usage project exists. Shown as a label on Pipeline rows. */
export type Origin = "requested" | "renewal" | "identified";

export type UsageStatus = "connected" | "partial" | "not_connected";

export type UsageCategory =
    | "Software"
    | "Cloud and data"
    | "Observability and security"
    | "AI"
    | "Market data";

/** Where a usage number cluster comes from, stated under it in one line. */
export interface Coverage {
    source: string;
    asOf: string;
    matchedPct?: number;
    note?: string;
}

export interface UsageVendor {
    id: string;
    name: string;
    category: UsageCategory;
    /** The brand domain the logo is looked up by. */
    website: string;
    /** The logo URL, derived from the website at load. */
    hero?: string;
}

/** A deal (an agreement with a vendor), the unit the Portfolio tallies. */
export interface UsageDeal {
    id: string;
    vendorId: string;
    name: string;
    type: ContractType;
    /** USD a year. */
    annualCost: number;
    term: { start?: string; renews: string; noticeBy: string };
    /** Negotiation owner, as initials. */
    owner: string;
    usageStatus: UsageStatus;
    /** Seat deals: licenses bought and the users active in the last 90 days; the utilization is their ratio. */
    seats?: { bought: number; active: number };
    /** Seat deals: active users over licenses bought. */
    utilizationPct?: number;
    /** Consumption deals: pace of consumption against the commitment, over the last 90 days. */
    pacePct?: number;
    /**
     * Consumption deals: the commitment year's state. Consumed to date defaults
     * to the pace held all year; the on-demand premium is what consumption past
     * the commitment costs on top.
     */
    commitment?: { consumedToDate?: number; onDemandPremiumPct: number };
    /** USD a year the analysis found at stake. Only once an analysis ran. */
    valueAtStake?: number;
    /** USD a year paid for capacity that is not used. From the connection, analysis or not. */
    unusedCapacity?: number;
    /** Short secondary reading of the usage: "8,900 of 12,400". */
    usageDetail?: string;
    coverage?: Coverage;
}

/** Proposed: the system opened it with an opportunity snapshot; the user has yet to decide. */
export type ProjectStage = "proposed" | "usage_analysis" | "playbook" | "negotiating" | "closed";

export type AnalysisKind = "snapshot" | "usage" | "playbook";

export interface UsageAnalysisRevision {
    kind: AnalysisKind;
    revision: number;
    publishedAt: string;
    /** The usage data the revision is based on. */
    usageTo?: string;
    /** For a playbook: the negotiation round it covers. */
    round?: number;
    inProgress?: boolean;
}

export type ActionOwnerKind = "user" | "teammate" | "function_owner" | "infinityloop";

export type ActionItemStatus = "open" | "done" | "canceled";

export interface ActionItem {
    id: string;
    projectId: string;
    /** The analysis (stage) the item belongs to; all of a stage's items end before the next stage. */
    stage: AnalysisKind;
    title: string;
    /** Why this item exists: who raised it and the one-line reason. */
    origin: Origin;
    why: string;
    ownerKind: ActionOwnerKind;
    ownerLabel: string;
    due: string;
    status: ActionItemStatus;
    note?: string;
    /** Items that read as one on the Pipeline when they share the earliest due date. */
    group?: string;
    /** What the platform does when the item is acted on: a button at the end of its row. */
    action?: PlatformAction;
}

export type PlatformAction = "request_analysis" | "request_playbook";

export const PLATFORM_ACTION_LABEL: Record<PlatformAction, string> = {
    request_analysis: "Request usage analysis",
    request_playbook: "Request playbook",
};

export interface UsageProject {
    id: string;
    /** As the hub names projects: what the work is, with the vendor under it. */
    name: string;
    dealId: string;
    origin: Origin;
    /** Why the project exists, in one short line. */
    trigger: string;
    stage: ProjectStage;
    /** USD a year. */
    estSavings: number;
    /** Replaces the plain amount when the saving reads differently ("$0.2M avoided"). */
    estSavingsLabel?: string;
    /** USD a year realized so far in the negotiation; on the table until the project closes. */
    realizedSavings?: number;
    /** The snapshot's rough estimate, as a range; brackets the target. */
    snapshotEstimate?: [number, number];
    /** What usage optimization alone saves, per the usage analysis; the target adds the price lever. */
    usageSavings?: number;
    analyses: UsageAnalysisRevision[];
    actionItems: ActionItem[];
    /** Identified since the user's last visit: highlighted on the Pipeline. */
    isNew?: boolean;
}

// ---- Microsoft EA detail -------------------------------------------------

export interface SkuRow {
    sku: string;
    bought: number;
    assigned: number;
    active90: number;
    inactive: number;
    departed: number;
    /** Users whose use fits a lower edition: "2,100 to E3", or "none". */
    fitLower: string;
    /** USD a year, per license. */
    unitPrice: number;
    /** USD a year. */
    costAtRisk: number;
    recommendation: string;
}

export type ReviewStatus = { kind: "confirmed"; on: string } | { kind: "due"; on: string };

export interface FunctionCohort {
    function: string;
    activeUsers: number;
    fitE3: number;
    keepE5: number;
    awaitingReview: number;
    review: ReviewStatus;
}

export type PersonState = "awaiting_owner" | "confirmed" | "ready" | "exempt" | "released" | "downgraded" | "keep";

export interface PersonRow {
    id: string;
    function: string;
    employment: string;
    lastSignIn: string;
    daysInactive: number;
    /** Days used in the last 30, 60 and 90 days; undefined when active. */
    daysUsed?: { d30: number; d60: number; d90: number };
    /** E5-only features used, of 5. */
    e5Features: number;
    recommendation: string;
    state: PersonState;
}

export interface WaterfallStep {
    label: string;
    value: number;
    kind: "total" | "minus" | "plus";
}

export interface TrendPoint {
    month: string;
    bought: number;
    active: number;
    projected?: boolean;
}

export interface SeatDealDetail {
    dealId: string;
    /** Report banner on the live page: what moved since the last report. */
    driftLine: string;
    paidFor: { licenses: number; assigned: number; neverAssigned: number; unitPrice: number };
    used: { active90: number; noSignIn90: number; departed: number; fitE3: number };
    needed: { total: number; e5: number; e3: number; e5Range: [number, number]; e3UnitPrice: number };
    waterfall: WaterfallStep[];
    skus: SkuRow[];
    cohorts: FunctionCohort[];
    people: PersonRow[];
    escalation: string;
    renewalEffect: { function: string; seats: number; allRelease: number; halfRelease: number; noneRelease: number; per100: number };
    /** The reclamation rules the workbench applies, as label and value. */
    rules: { label: string; value: string }[];
    trend: TrendPoint[];
    analysisNotes: string[];
}

// ---- Snowflake capacity detail -------------------------------------------

export interface ConsumptionMonth {
    /** "2026-04". */
    month: string;
    /** Cumulative USD consumed by the month's end (or to date for the current month). */
    consumed: number;
}

export interface WorkloadRow {
    workload: string;
    sharePct: number;
    /** USD a year, the last 90 days annualized. */
    annualized: number;
    /** Change since July, percent. */
    sinceJulyPct: number;
}

export interface ConsumptionDealDetail {
    dealId: string;
    monthly: ConsumptionMonth[];
    workloads: WorkloadRow[];
    /** The one line the workloads explain. */
    insight: string;
    terms: { capacityRate: string; onDemandRate: string; rollover: string; earlyRenewal: string };
}
